import React, { useRef, useCallback } from 'react';
import { Star, Layers, CheckCircle, Eye, RefreshCw, Wrench } from 'lucide-react';

const reasons = [
  {
    icon: <Star className="w-7 h-7 text-amber-400" />,
    title: 'Comprehensive & Specialized Services',
    description:
      'From luxury residences to commercial hubs and hospitality spaces—our multidisciplinary team delivers expert solutions across every sector, all under one roof.',
  },
  {
    icon: <Layers className="w-7 h-7 text-amber-400" />,
    title: 'Tailored Design Approach',
    description:
      'Every project is unique. We listen, understand, and design with intent—balancing function, aesthetics, and your aspirations to create spaces that truly resonate.',
  },
  {
    icon: <CheckCircle className="w-7 h-7 text-amber-400" />,
    title: 'End-to-End Turnkey Execution',
    description:
      'From concept development to final handover, we manage the entire journey with PMC and Turnkey solutions for a seamless, stress-free experience.',
  },
  {
    icon: <Eye className="w-7 h-7 text-amber-400" />,
    title: '3D Spatial Visualization & VR',
    description:
      'Visualize your space before construction begins through photorealistic 3D renders, animated walkthroughs, VR presentations, and BIM modeling integration.',
  },
  {
    icon: <Wrench className="w-7 h-7 text-amber-400" />,
    title: 'Precision in Craftsmanship',
    description:
      'Our commitment to detail, material quality, and rigorous on-site supervision results in flawless execution—bringing luxury and longevity to every space.',
  },
  {
    icon: <RefreshCw className="w-7 h-7 text-amber-400" />,
    title: 'Transformative Renovation & Landscape',
    description:
      'We don’t just build—we reimagine. Whether revitalizing interior layouts or enhancing outdoor landscapes, we breathe new life into existing environments.',
  },
];

const TiltReasonCard = ({ reason, index }) => {
  const ref = useRef(null);
  const onMouseMove = useCallback((e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width  - 0.5;
    const y = (e.clientY - rect.top)  / rect.height - 0.5;
    el.style.transform = `perspective(900px) rotateY(${x * 10}deg) rotateX(${-y * 8}deg) scale(1.03)`;
    el.style.boxShadow = `${-x * 15}px ${y * 15}px 40px rgba(0,0,0,0.30), 0 0 0 1px rgba(212,175,55,0.30)`;
  }, []);
  const onMouseLeave = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.transform = 'perspective(900px) rotateY(0deg) rotateX(0deg) scale(1)';
    el.style.boxShadow = '';
    el.style.transition = 'transform 0.6s ease, box-shadow 0.6s ease';
  }, []);

  return (
    <div
      ref={ref}
      className={`reveal-depth delay-${(index % 3) + 1} bg-white/5 border border-amber-300/20 backdrop-blur-xl p-8 rounded-2xl shadow-xl hover:border-amber-400/50 transition-all duration-300 will-change-transform`}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
    >
      <div className="flex items-center space-x-4 mb-4">
        <div className="p-3 bg-amber-400/10 border border-amber-400/30 rounded-xl flex-shrink-0">
          {reason.icon}
        </div>
        <h3 className="text-xl font-bold font-serif text-white leading-snug">
          {reason.title}
        </h3>
      </div>
      <p className="text-gray-300 text-sm leading-relaxed">{reason.description}</p>
    </div>
  );
};

const WhyChoose = () => {
  return (
    <section
      className="py-24 relative overflow-hidden text-white"
      style={{
        backgroundColor: '#021a13',
        background: 'linear-gradient(180deg, #021a13 0%, #04281c 50%, #021a13 100%)',
        color: "#ffffff"
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16 reveal-left">
          <p className="font-serif text-xs uppercase tracking-widest text-amber-400 mb-2">The Sakae Distinction</p>
          <h2 className="text-4xl md:text-5xl font-serif font-normal text-white mb-4">
            Why Choose Sakae Studio?
          </h2>
          <div className="w-20 h-0.5 bg-gradient-to-r from-amber-600 via-amber-400 to-amber-600 mx-auto mb-4"></div>
          <p className="text-base text-gray-300 max-w-2xl mx-auto">
            At Sakae – Opulent Architecture, we do more than design—we craft lifestyle experiences through spatial innovation and precision execution.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {reasons.map((reason, index) => (
            <TiltReasonCard key={index} reason={reason} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyChoose;